import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Upload01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Permissions } from 'constants/permissions';
import { shouldPromptFcraConsent } from 'helpers/checks';
import { useDocumentTitle } from 'hooks/document-title';
import { useFCRAConsentModal } from 'hooks/use-fcra-consent-modal';
import AppPage from 'layouts/AppPage/AppPage';
import toast from 'react-hot-toast';
import { useCSVReader } from 'react-papaparse';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { StepSelector } from 'views/NewCheck/components/StepSelector/StepSelector';

import { ChecksSelector } from 'components/ChecksSelector/ChecksSelector';
import { CsvTableDetails } from 'components/CsvTableDetails/CsvTableDetails';
import { Button } from 'components/FormComponents';
import { StepHeader } from 'components/StepHeader/StepHeader';
import { CustomToast } from 'components/Toast/CustomToast';

import { useBillingEntityOptionSet } from 'api/billing-api';
import { getBrands, selectBrands } from 'api/brands-slice';
import { getBundles, selectBundles } from 'api/bundles-slice';
import {
  useBulkImportMutation,
  useBulkPreviewMutation,
  useDownloadBulkImportTemplateMutation,
} from 'api/candidates-api';
import { useGetCheckTypesQuery } from 'api/check-types-api';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { selectCompany } from 'api/company-slice';
import { getForms } from 'api/forms-slice';
import { selectForms } from 'api/forms-slice';

import { AlreadyImportedAlert } from './AlreadyImportedAlert';
import { DuplicatesReview } from './DuplicatesReview';
import { ValidationIssuesReview } from './ValidationIssuesReview';
import { getImportCount } from './duplicateDecisionUtils';

// Inner component so we can set fileNameRef in an effect (avoids ref mutation during render)
function CsvReaderDropzone({ getRootProps, acceptedFile, fileNameRef, children }) {
  useLayoutEffect(() => {
    if (acceptedFile?.name && fileNameRef) {
      fileNameRef.current = acceptedFile.name;
    }
  }, [acceptedFile?.name, fileNameRef]);
  return (
    <div
      {...getRootProps()}
      className="flex justify-center items-center h-40 w-full rounded-[10px] border border-dashed border-border-secondary bg-white cursor-pointer transition-[border-color] duration-200 hover:border-brand-primary">
      {children}
    </div>
  );
}

export const BulkImport = ({ type = 'candidates' }) => {
  const billingEntityOptionSet = useBillingEntityOptionSet();
  useDocumentTitle('Bulk Import');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const forms = useSelector(selectForms);
  const company = useSelector(selectCompany);
  const companyChecks = useSelector(selectCompanyChecks);
  const brands = useSelector(selectBrands);
  const bundles = useSelector(selectBundles);
  const [bulkImport] = useBulkImportMutation();
  const [bulkPreview] = useBulkPreviewMutation();
  const [downloadTemplate, { isLoading: templateDownloading }] =
    useDownloadBulkImportTemplateMutation();
  const { data: checkTypes } = useGetCheckTypesQuery();

  const selectorRef = useRef(null);
  /** File name from CSVReader render prop (onUploadAccepted only receives Papa results, not the file). */
  const acceptedFileNameRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [previewResult, setPreviewResult] = useState(null);
  const [pendingImportData, setPendingImportData] = useState(null);
  const [duplicateDecisions, setDuplicateDecisions] = useState([]);
  const [alreadyImported, setAlreadyImported] = useState(null);
  const lastImportRef = useRef(null);

  const [csvData, setCsvData] = useState();
  const [csvFileName, setCsvFileName] = useState(null);

  const [valid, setValid] = useState(false);

  const importCount = useMemo(
    () => getImportCount(previewResult, duplicateDecisions),
    [previewResult, duplicateDecisions],
  );

  const { CSVReader } = useCSVReader();

  // Initialization
  useEffect(() => {
    Promise.all([dispatch(getBrands()), dispatch(getForms()), dispatch(getBundles())]).then(_ =>
      setLoading(false),
    );
  }, []);

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate({ renewal: type === 'renewals' }).unwrap();
    } catch (e) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            type="error"
            title="Download failed"
            message={e?.data?.message ?? e?.message ?? 'Could not download template'}
          />
        ),
        { duration: 4000 },
      );
    }
  };

  const handleConsentSubmit = (data, checksData) => {
    handleSubmit({ ...checksData, consents: data });
  };

  const { showModal: showFCRAConsentModal, FCRAConsentModal } = useFCRAConsentModal({
    onConsentSubmit: handleConsentSubmit,
    isBatchImport: true,
  });

  const handleBulkImportSubmit = async checksData => {
    const hasFCRA = shouldPromptFcraConsent(
      checksData?.checks || [],
      companyChecks,
      checkTypes,
      company?.country,
    );

    if (hasFCRA) {
      showFCRAConsentModal(checksData);
    } else {
      handleSubmit(checksData);
    }
  };

  const handleSubmit = async checksData => {
    setLoading(true);
    const data = {
      csv_data: csvData,
      checks: checksData.checks,
      candidate_data: checksData.candidate,
      renewal: type === 'renewals',
      ...(checksData?.consents && { consents: checksData.consents }),
    };

    const { data: previewResponse, error: previewError } = await bulkPreview(data);
    setLoading(false);

    if (previewError) {
      const message = previewError?.data?.message ?? previewError?.message ?? 'Preview failed';
      toast.custom(
        t => <CustomToast t={t} type="error" title="Preview failed" message={message} />,
        { duration: 4000 },
      );
      return;
    }

    if (previewResponse?.error) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            type="error"
            title="Error"
            message={previewResponse.error?.message ?? 'Preview failed'}
          />
        ),
        { duration: 4000 },
      );
      return;
    }

    const result = previewResponse?.result;
    if (!result) return;

    // Always go to Step 2 (Review & Import). Import runs only after "Confirm & Import" on step 2.
    setPendingImportData(data);
    setPreviewResult(result);
    setDuplicateDecisions(
      [...(result.exact_duplicates ?? []), ...(result.possible_duplicates ?? [])].map(dup => ({
        csv_index: dup.csv_index,
        action: 'skip',
        existing_id: dup.existing?.id ?? undefined,
      })),
    );
    setStep(2);
  };

  const handleImport = async (data, duplicateDecisions, force = false) => {
    lastImportRef.current = { data, duplicateDecisions };
    setLoading(true);
    const { data: response, error: mutationError } = await bulkImport({
      ...data,
      duplicate_decisions: duplicateDecisions,
      ...(force && { force: true }),
    });
    setLoading(false);

    if (mutationError) {
      const message = mutationError?.data?.message ?? mutationError?.message ?? 'Import failed';
      toast.custom(
        t => <CustomToast t={t} type="error" title="Import failed" message={message} />,
        { duration: 4000 },
      );
      return;
    }

    if (response?.error) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            type="error"
            title="Import failed"
            message={response.error?.message ?? 'Import failed'}
          />
        ),
        { duration: 4000 },
      );
      return;
    }

    const result = response?.result;
    if (result?.already_processed) {
      setAlreadyImported(result);
      return;
    }

    const parts = [];
    if (result?.imported > 0) parts.push(`${result.imported} imported`);
    if (result?.added_to_existing > 0) parts.push(`${result.added_to_existing} added to existing`);
    if (result?.skipped > 0) parts.push(`${result.skipped} skipped`);
    if (result?.invalid > 0) parts.push(`${result.invalid} invalid`);
    const message = parts.length > 0 ? parts.join(', ') : 'Import complete';

    toast.custom(t => <CustomToast t={t} type="success" title="Success" message={message} />, {
      duration: 4000,
    });
    navigate('/dashboard');
  };

  const handleConfirmReview = async (decisions = null) => {
    // If no decisions passed, default to skipping all duplicate rows (exact + possible)
    const resolved =
      decisions != null
        ? decisions
        : (() => {
            const exact = previewResult?.exact_duplicates ?? [];
            const possible = previewResult?.possible_duplicates ?? [];
            return [...exact, ...possible].map(dup => ({
              csv_index: dup.csv_index,
              action: 'skip',
            }));
          })();
    await handleImport(pendingImportData, resolved);
  };

  const handleBackToUpload = () => {
    setStep(1);
    setPreviewResult(null);
    setPendingImportData(null);
    setDuplicateDecisions([]);
  };

  const onUploadAccepted = results => {
    setCsvData(results.data);
    setCsvFileName(acceptedFileNameRef.current ?? null);
    acceptedFileNameRef.current = null;
  };

  return (
    <AppPage loading={loading} requiredPermissions={[Permissions.CandidateWrite]}>
      <div className="flex justify-center items-center flex-col">
        {FCRAConsentModal}
        <AppPage.Header
          title={type === 'renewals' ? 'Bulk Import Renewals' : 'Bulk Import Candidates'}
          subtitle="Upload a CSV, choose check types, then review and confirm the import."
          className="u-divider-bottom-border"
        />
        <div className="flex justify-center items-center flex-col">
          <div className={cn('w-[450px] overflow-hidden', 'u-margin-y--large')}>
            <StepSelector
              className="u-margin-bottom--large"
              activeStep={step}
              onStep1Selection={step === 2 ? handleBackToUpload : undefined}
              onStep2Selection={() => {}}
            />
            <div className={cn('card', 'card-with-border')}>
              <StepHeader
                step={step}
                title={step === 1 ? 'Upload CSV' : 'Review & Import'}
                subtitle={
                  step === 1
                    ? 'Drop your CSV or download the template to get started.'
                    : 'Review duplicates before completing the import.'
                }
              />
              <div
                className={cn(
                  'background-secondary',
                  'u-padding',
                  'card_border--top',
                  'text-lg text-text-primary',
                )}>
                {step === 1 ? (
                  <>
                    <div className={cn('u-width-100', 'u-margin-bottom--large')}>
                      {csvData ? (
                        <CsvTableDetails
                          csvData={csvData}
                          fileName={csvFileName}
                          onDrop={() => {
                            setCsvData(null);
                            setCsvFileName(null);
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-start gap-2.5 w-full">
                          <CSVReader onUploadAccepted={onUploadAccepted}>
                            {({ getRootProps, acceptedFile }) => (
                              <CsvReaderDropzone
                                getRootProps={getRootProps}
                                acceptedFile={acceptedFile}
                                fileNameRef={acceptedFileNameRef}>
                                <div className="flex flex-col justify-center items-center gap-1.5 w-full h-full">
                                  <HugeiconsIcon
                                    icon={Upload01Icon}
                                    size={32}
                                    strokeWidth={1.5}
                                    className="text-grey-35"
                                  />
                                  <p className="text-grey-35 m-0 text-lg">
                                    Drop your CSV here, or{' '}
                                    <span className="text-brand-primary">browse</span>
                                  </p>
                                </div>
                              </CsvReaderDropzone>
                            )}
                          </CSVReader>
                          <div className="flex flex-col items-start gap-1">
                            <button
                              type="button"
                              className="u-link dark text-xl bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={templateDownloading}
                              onClick={handleDownloadTemplate}
                              data-testid="bulk-import-download-template-button">
                              {templateDownloading ? 'Preparing template…' : 'Download template'}
                            </button>
                            <p className="m-0 text-grey-35 text-lg max-w-full">
                              Template includes your company&apos;s custom field columns when
                              configured (header names{' '}
                              <code className="text-text-primary">custom_field_1</code>, etc.).
                            </p>
                            <a
                              className="u-link dark text-lg"
                              target="_blank"
                              rel="noopener noreferrer"
                              href={`https://cdn.checkmate.tech/checkmate-files/assets/${type === 'renewals' ? 'renewals_bulk_import.csv' : 'candidates_bulk_import.csv'}`}>
                              Generic template (no custom fields)
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="[&_.u-divider]:hidden">
                      <ChecksSelector
                        ref={selectorRef}
                        forms={forms}
                        brands={brands}
                        bundles={bundles}
                        company={company}
                        companyChecks={companyChecks}
                        billingEntityOptionSet={billingEntityOptionSet}
                        setValidity={setValid}
                        onSubmit={handleBulkImportSubmit}
                        bulk={true}
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col gap-8">
                    <ValidationIssuesReview validationIssues={previewResult?.validation_issues} />
                    <DuplicatesReview
                      previewResult={previewResult}
                      decisions={duplicateDecisions}
                      onDecisionsChange={setDuplicateDecisions}
                    />
                  </div>
                )}
              </div>
              <div className={cn('card_footer', 'u-flex-box', 'u-flex-justify-between')}>
                {step === 1 ? (
                  <>
                    <Button type="secondary" onClick={() => navigate('/dashboard')}>
                      Cancel
                    </Button>
                    <Button
                      onClick={() => selectorRef.current.submit()}
                      disabled={!valid || !csvData}
                      dataTestId="bulk-import-continue-button">
                      Continue
                    </Button>
                  </>
                ) : (
                  <>
                    <Button type="secondary" onClick={handleBackToUpload} disabled={loading}>
                      Back
                    </Button>
                    <Button
                      onClick={() => handleConfirmReview(duplicateDecisions)}
                      disabled={!previewResult || importCount === 0 || loading}
                      loading={loading}
                      dataTestId="bulk-import-confirm-button">
                      {importCount > 0
                        ? `Confirm & Import (${importCount} ${importCount === 1 ? 'candidate' : 'candidates'})`
                        : 'Nothing to import'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <AlreadyImportedAlert
        result={alreadyImported}
        onClose={() => setAlreadyImported(null)}
        onForce={() => {
          setAlreadyImported(null);
          handleImport(lastImportRef.current.data, lastImportRef.current.duplicateDecisions, true);
        }}
      />
    </AppPage>
  );
};

export default BulkImport;
